<template>
    <!-- Backdrop -->
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="$emit('close')">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

        <!-- Modal -->
        <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

            <!-- Header -->
            <div class="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div>
                    <h2 class="text-lg font-black text-gray-900 tracking-tight">Nouvelle démarche</h2>
                    <p class="text-xs text-gray-400 mt-0.5">Ajoutez un prospect à votre pipeline</p>
                </div>
                <button @click="$emit('close')" class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
                    <i class="fas fa-times text-sm"></i>
                </button>
            </div>

            <!-- Body -->
            <div class="flex-1 overflow-y-auto px-6 py-5 space-y-6">

                <!-- Infos générales -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <!-- Nom société -->
                    <div class="sm:col-span-2">
                        <label class="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                            Nom de la société <span class="text-red-500">*</span>
                        </label>
                        <input
                            v-model="form.company_name"
                            type="text"
                            placeholder="Ex : Acme Corp"
                            class="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition placeholder-gray-300"
                            :class="errors.company_name ? 'border-red-400 ring-1 ring-red-400' : ''"
                        />
                        <p v-if="errors.company_name" class="text-xs text-red-500 mt-1"><i class="fas fa-exclamation-circle mr-1"></i>{{ errors.company_name }}</p>
                    </div>

                    <!-- Statut -->
                    <div>
                        <label class="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Statut</label>
                        <div class="relative">
                            <select
                                v-model="form.status"
                                class="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent appearance-none bg-white transition">
                                <option v-for="(cfg, key) in statusOptions" :key="key" :value="key">{{ cfg.label }}</option>
                            </select>
                            <i class="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                        </div>
                    </div>

                    <!-- Effectif -->
                    <div>
                        <label class="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Effectif</label>
                        <div class="flex gap-2">
                            <button
                                v-for="eff in effectifOptions"
                                :key="eff"
                                @click="form.effectif = eff"
                                type="button"
                                class="flex-1 py-2 rounded-xl text-xs font-bold border transition-all"
                                :class="form.effectif === eff
                                    ? 'bg-orange-500 text-white border-orange-500 shadow-sm shadow-orange-200'
                                    : 'bg-white text-gray-500 border-gray-200 hover:border-orange-300 hover:text-orange-500'">
                                {{ eff }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Contacts -->
                <div>
                    <div class="flex items-center justify-between mb-3">
                        <label class="text-xs font-bold text-gray-700 uppercase tracking-wide">
                            <i class="fas fa-users text-orange-400 mr-1.5"></i>Contacts
                        </label>
                        <button
                            @click="addContact"
                            type="button"
                            class="flex items-center gap-1.5 text-xs font-bold text-orange-500 hover:text-orange-600 transition-colors">
                            <i class="fas fa-plus text-[10px] bg-orange-100 rounded-full py-1.5 pl-2 pr-4"></i>
                            Ajouter un contact
                        </button>
                    </div>

                    <div v-if="form.contacts.length === 0" class="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
                        <i class="fas fa-user-plus text-gray-300 text-2xl mb-2"></i>
                        <p class="text-xs text-gray-400">Aucun contact ajouté</p>
                        <button @click="addContact" type="button" class="mt-2 text-xs font-bold text-orange-500 hover:text-orange-600">
                            Ajouter le premier contact
                        </button>
                    </div>

                    <div v-else class="space-y-3">
                        <div
                            v-for="(contact, i) in form.contacts"
                            :key="i"
                            class="bg-gray-50 rounded-xl p-4 border border-gray-100 relative group">

                            <button
                                @click="removeContact(i)"
                                type="button"
                                class="absolute top-3 right-3 w-6 h-6 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100">
                                <i class="fas fa-times text-xs"></i>
                            </button>

                            <div class="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                                <i class="fas fa-user text-orange-400 mr-1"></i> Contact {{ i + 1 }}
                            </div>

                            <div class="grid grid-cols-2 gap-3">
                                <div>
                                    <label class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Prénom</label>
                                    <input v-model="contact.prenom" type="text" placeholder="Jean" class="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition placeholder-gray-300"/>
                                </div>
                                <div>
                                    <label class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Nom</label>
                                    <input v-model="contact.nom" type="text" placeholder="Dupont" class="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition placeholder-gray-300"/>
                                </div>
                                <div>
                                    <label class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Email</label>
                                    <input v-model="contact.email" type="email" placeholder="jean@acme.com" class="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition placeholder-gray-300"/>
                                </div>
                                <div>
                                    <label class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Téléphone</label>
                                    <input v-model="contact.telephone" type="tel" placeholder="06 00 00 00 00" class="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition placeholder-gray-300"/>
                                </div>
                                <div>
                                    <label class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Poste</label>
                                    <input v-model="contact.poste" type="text" placeholder="Directeur général" class="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition placeholder-gray-300"/>
                                </div>
                                <div>
                                    <label class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Note</label>
                                    <input v-model="contact.note" type="text" placeholder="Note libre..." class="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition placeholder-gray-300"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                <button
                    @click="$emit('close')"
                    type="button"
                    class="px-5 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all">
                    Annuler
                </button>
                <button
                    @click="submit"
                    :disabled="loading"
                    type="button"
                    class="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-xl shadow-sm shadow-orange-200 hover:shadow-md hover:shadow-orange-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                    <i v-if="loading" class="fas fa-spinner fa-spin text-xs"></i>
                    <i v-else class="fas fa-check text-xs"></i>
                    {{ loading ? 'Création...' : 'Créer le prospect' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'NouvelleDeMarche',
    props: {
        defaultStatus: { default: 'nouveau' }
    },
    data() {
        return {
            loading: false,
            form: {
                company_name: '',
                status: this.defaultStatus,
                effectif: null,
                contacts: [],
            },
            errors: {},
            statusOptions: {
                nouveau:     { label: 'Nouveau' },
                contacte:    { label: 'Contacté' },
                en_cours:    { label: 'En cours' },
                proposition: { label: 'Proposition' },
                negociation: { label: 'Négociation' },
                relance:     { label: 'Relance' },
            },
            effectifOptions: ['1', '2-5', '5-10', '10+'],
        };
    },
    methods: {
        addContact() {
            this.form.contacts.push({ prenom: '', nom: '', email: '', telephone: '', poste: '', note: '' });
        },
        removeContact(i) {
            this.form.contacts.splice(i, 1);
        },
        validate() {
            this.errors = {};
            if (!this.form.company_name.trim()) {
                this.errors.company_name = 'Le nom de la société est requis';
            }
            return Object.keys(this.errors).length === 0;
        },
        async submit() {
            if (!this.validate()) return;
            this.loading = true;
            const res = await this.$request.post('/commercial/prospects', {
                ...this.form,
                contacts: this.form.contacts.filter(c => c.prenom || c.nom || c.email)
            });
            this.loading = false;
            if (res && res.prospect) {
                this.$emit('created', res.prospect);
            }
        }
    }
};
</script>